import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

import { AuthProvider } from "@/components/AuthContext";
import { ProAuthProvider } from "@/components/ProAuthContext";
import { ImProvider } from "@/im/ImProvider";
import ProtectedRoute from "@/components/ProtectedRoute";
import ProProtectedRoute from "@/components/ProProtectedRoute";
import GlobalErrorBoundary from "@/components/GlobalErrorBoundary";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PartnersBanner from "@/components/PartnersBanner";

import Home from "@/pages/Home";
import Produit from "@/pages/Produit";
import Solutions from "@/pages/Solutions";
import Plateforme from "@/pages/Plateforme";
import PlateformeModule from "@/pages/PlateformeModule";
import ServiceDmp from "@/pages/services/ServiceDmp";
import CertificationCnda from "@/pages/services/CertificationCnda";
import ServiceIns from "@/pages/services/ServiceIns";
import ServicePsc from "@/pages/services/ServicePsc";
import ServiceFhir from "@/pages/services/ServiceFhir";
import Conformite from "@/pages/Conformite";
import NetHome from "@/pages/net/NetHome";
import NetPersona from "@/pages/net/NetPersona";
import DemoPage from "@/pages/Demo";
import SupportDistant from "@/pages/SupportDistant";
import BlogList from "@/pages/BlogList";
import BlogPost from "@/pages/BlogPost";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminStudioProjects from "@/pages/AdminStudioProjects";
import AdminEditor from "@/pages/AdminEditor";
import AdminMssante from "@/pages/AdminMssante";
import AdminDemoRequests from "@/pages/AdminDemoRequests";
import AdminSupport from "@/pages/AdminSupport";
import AdminBilling from "@/pages/AdminBilling";
import AdminSeo from "@/pages/AdminSeo";
import AdminDemandesMss from "@/pages/AdminDemandesMss";
import NotFound from "@/pages/NotFound";
import Mssante from "@/pages/Mssante";
import Osmose from "@/pages/Osmose";
import OhMyVueCommandeMss from "@/pages/OhMyVueCommandeMss";
import ProOptic from "@/pages/ProOptic";
import IVoirNet from "@/pages/IVoirNet";
import Download from "@/pages/Download";
import DownloadCheck from "@/pages/DownloadCheck";
import DownloadDiagnostic from "@/pages/DownloadDiagnostic";
import ProOpticMessageType from "@/pages/ProOpticMessageType";
import ProOpticDmpPage from "@/pages/ProOpticDmpPage";
import Agenda from "@/pages/Agenda";
import MessageType from "@/pages/MessageType";
import DmpPage from "@/pages/DmpPage";
import DossierPage from "@/pages/DossierPage";
import AgendaType from "@/pages/AgendaType";
import MssanteOpticien from "@/pages/mssante/MssanteOpticien";
import MssanteAudioprothesiste from "@/pages/mssante/MssanteAudioprothesiste";
import MssanteProfessionnels from "@/pages/mssante/MssanteProfessionnels";
import MssanteMedecin from "@/pages/mssante/MssanteMedecin";
import MssanteInfirmierLiberal from "@/pages/mssante/MssanteInfirmierLiberal";
import MssanteAssureurs from "@/pages/MssanteAssureurs";
import MyoControlApp from "@/myo/MyoControlApp";
import LutteFraudeSante from "@/pages/LutteFraudeSante";
import Studio from "@/pages/Studio";
import StudioPreview from "@/pages/StudioPreview";
import StudioPersona from "@/pages/StudioPersona";
import MssanteInscription from "@/pages/MssanteInscription";
import MssanteConnexion from "@/pages/MssanteConnexion";
import MssanteActivation from "@/pages/MssanteActivation";
import MssanteBienvenue from "@/pages/MssanteBienvenue";
import MssanteEspace from "@/pages/MssanteEspace";
import MssanteCreerMessagerie from "@/pages/MssanteCreerMessagerie";
import MssanteStructures from "@/pages/MssanteStructures";
import MssanteAcces from "@/pages/MssanteAcces";
import MssanteUserAcces from "@/pages/MssanteUserAcces";
import MssanteSecurite from "@/pages/MssanteSecurite";
import MssanteMessagerieInstantanee from "@/pages/MssanteMessagerieInstantanee";
import MssanteTelesoin from "@/pages/MssanteTelesoin";
import MssanteFacturation from "@/pages/MssanteFacturation";
import StudioGenerating from "@/pages/StudioGenerating";
import MentionsLegales from "@/pages/MentionsLegales";
import CGU from "@/pages/CGU";
import CGV from "@/pages/CGV";
import Contrat from "@/pages/Contrat";
import ProtectionDonnees from "@/pages/ProtectionDonnees";
import CookieBanner from "@/components/CookieBanner";
import PageViewTracker from "@/components/PageViewTracker";
import { initAnalytics } from "@/lib/analytics";
import { Toaster } from "@/components/ui/sonner";
import AdminBlog from "@/pages/AdminBlog";

// ProOptic a été renommé IDM Optic : les anciennes adresses /prooptic redirigent vers /idm-optic
function RedirectionIdmOptic({ to }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [pathname, hash]);
  return null;
}

function PublicShell({ children }) {
  return (
    <>
      <Header />
      <main className="pt-0">{children}</main>
      <PartnersBanner />
      <Footer />
    </>
  );
}

function AdminShell({ children }) {
  // No public header/footer on admin (cleaner UI)
  // index.html no longer carries a static <title> (Helmet duplication fix)
  // — admin pages don't render <SEO>, so set the tab title here.
  useEffect(() => {
    if (!document.querySelector("head > title")) {
      document.title = "Admin · MetaConnect";
    }
  }, []);
  return <main>{children}</main>;
}

export default function App() {
  useEffect(() => { initAnalytics(); }, []);
  return (
    <HelmetProvider>
      <GlobalErrorBoundary>
        <AuthProvider>
          <ProAuthProvider>
            <ImProvider>
            <div className="App">
              <BrowserRouter>
              <PageViewTracker />
              <ScrollManager />
              <Routes>
                {/* Public site */}
                <Route path="/" element={<PublicShell><Home /></PublicShell>} />
                <Route path="/produit" element={<PublicShell><Produit /></PublicShell>} />
                <Route path="/solutions" element={<PublicShell><Solutions /></PublicShell>} />
                <Route path="/plateforme" element={<PublicShell><Plateforme /></PublicShell>} />
                <Route path="/plateforme/:moduleId" element={<PublicShell><PlateformeModule /></PublicShell>} />
                <Route path="/services/dmp" element={<PublicShell><ServiceDmp /></PublicShell>} />
                <Route path="/services/dmp/certification-cnda" element={<PublicShell><CertificationCnda /></PublicShell>} />
                <Route path="/services/ins" element={<PublicShell><ServiceIns /></PublicShell>} />
        <Route path="/services/psc" element={<PublicShell><ServicePsc /></PublicShell>} />
                &
            &
            <Route path="/message-type" element={<PublicShell><MessageType /></PublicShell>} />
            <Route path="/dmp" element={<PublicShell><DmpPage /></PublicShell>} />
            <Route path="/dossier-patient" element={<PublicShell><DossierPage /></PublicShell>} />
            <Route path="/agenda" element={<PublicShell><Agenda /></PublicShell>} />
        <Route path="/agenda-type" element={<PublicShell><AgendaType /></PublicShell>} />
                <Route path="/services/fhir" element={<PublicShell><ServiceFhir /></PublicShell>} />
                <Route path="/conformite" element={<PublicShell><Conformite /></PublicShell>} />
                <Route path="/net" element={<PublicShell><NetHome /></PublicShell>} />
                <Route path="/net/:slug" element={<PublicShell><NetPersona /></PublicShell>} />
                <Route path="/demo" element={<PublicShell><DemoPage /></PublicShell>} />
                <Route path="/support-distant" element={<PublicShell><SupportDistant /></PublicShell>} />
                <Route path="/blog" element={<PublicShell><BlogList /></PublicShell>} />
                <Route path="/blog/:slug" element={<PublicShell><BlogPost /></PublicShell>} />

                {/* MSSanté — public */}
                <Route path="/mssante" element={<PublicShell><Mssante /></PublicShell>} />
                <Route path="/osmose" element={<Osmose />} />
                <Route path="/ohmyvue/commande-mssante" element={<OhMyVueCommandeMss />} />
                <Route path="/idm-optic" element={<ProOptic />} />
          <Route path="/prooptic" element={<RedirectionIdmOptic to="/idm-optic" />} />
                <Route path="/ivoirnet" element={<IVoirNet />} />
                <Route path="/idm-optic/message-type" element={<PublicShell><ProOpticMessageType /></PublicShell>} />
          <Route path="/prooptic/message-type" element={<RedirectionIdmOptic to="/idm-optic/message-type" />} />
                <Route path="/idm-optic/dmp" element={<PublicShell><ProOpticDmpPage /></PublicShell>} />
          <Route path="/prooptic/dmp" element={<RedirectionIdmOptic to="/idm-optic/dmp" />} />
                <Route path="/download" element={<PublicShell><Download /></PublicShell>} />
                <Route path="/download/verification" element={<PublicShell><DownloadCheck /></PublicShell>} />
                <Route path="/download/diagnostic" element={<PublicShell><DownloadDiagnostic /></PublicShell>} />
                <Route path="/mssante/opticien" element={<PublicShell><MssanteOpticien /></PublicShell>} />
                <Route path="/mssante/audioprothesiste" element={<PublicShell><MssanteAudioprothesiste /></PublicShell>} />
                <Route path="/mssante/professionnels-de-sante" element={<PublicShell><MssanteProfessionnels /></PublicShell>} />
                <Route path="/mssante-medecin" element={<PublicShell><MssanteMedecin /></PublicShell>} />
                <Route path="/mssante-infirmier-liberal" element={<PublicShell><MssanteInfirmierLiberal /></PublicShell>} />
                <Route path="/mssante-assureurs" element={<PublicShell><MssanteAssureurs /></PublicShell>} />
                <Route path="/lutte-fraude-sante" element={<PublicShell><LutteFraudeSante /></PublicShell>} />
                <Route path="/studio" element={<PublicShell><Studio /></PublicShell>} />
                <Route path="/studio/preview/:id" element={<StudioPreview />} />
                <Route path="/studio/generating/:id" element={<StudioGenerating />} />
                <Route path="/studio/:slug" element={<PublicShell><StudioPersona /></PublicShell>} />
                <Route path="/data-room/*" element={<MyoControlApp />} />
                <Route path="/mssante/inscription" element={<PublicShell><MssanteInscription /></PublicShell>} />
                <Route path="/mssante/connexion" element={<PublicShell><MssanteConnexion /></PublicShell>} />
                <Route path="/mssante/activation" element={<PublicShell><MssanteActivation /></PublicShell>} />

                {/* MSSanté — pro auth required */}
                <Route path="/mssante/bienvenue" element={<ProProtectedRoute><PublicShell><MssanteBienvenue /></PublicShell></ProProtectedRoute>} />
                <Route path="/mssante/espace" element={<ProProtectedRoute><PublicShell><MssanteEspace /></PublicShell></ProProtectedRoute>} />
                <Route path="/mssante/espace/structures" element={<ProProtectedRoute><PublicShell><MssanteStructures /></PublicShell></ProProtectedRoute>} />
                <Route path="/mssante/espace/messagerie/nouvelle" element={<ProProtectedRoute><PublicShell><MssanteCreerMessagerie /></PublicShell></ProProtectedRoute>} />
                {/* Alias to satisfy the welcome dashboard CTA "Créer ma boîte MSS" → /mssante/creer-boite */}
                <Route path="/mssante/creer-boite" element={<ProProtectedRoute><PublicShell><MssanteCreerMessagerie /></PublicShell></ProProtectedRoute>} />
                <Route path="/mssante/espace/messagerie/:id/acces" element={<ProProtectedRoute><PublicShell><MssanteAcces /></PublicShell></ProProtectedRoute>} />
                <Route path="/mssante/espace/utilisateurs/:uid/acces" element={<ProProtectedRoute><PublicShell><MssanteUserAcces /></PublicShell></ProProtectedRoute>} />
                <Route path="/mssante/espace/securite" element={<ProProtectedRoute><PublicShell><MssanteSecurite /></PublicShell></ProProtectedRoute>} />
                <Route path="/mssante/espace/messagerie-instantanee" element={<ProProtectedRoute><PublicShell><MssanteMessagerieInstantanee /></PublicShell></ProProtectedRoute>} />
                <Route path="/mssante/espace/telesoin" element={<ProProtectedRoute><PublicShell><MssanteTelesoin /></PublicShell></ProProtectedRoute>} />
                <Route path="/mssante/espace/facturation" element={<ProProtectedRoute><PublicShell><MssanteFacturation /></PublicShell></ProProtectedRoute>} />

                {/* Legal */}
                <Route path="/mentions-legales" element={<PublicShell><MentionsLegales /></PublicShell>} />
                <Route path="/cgu" element={<PublicShell><CGU /></PublicShell>} />
                <Route path="/cgv" element={<PublicShell><CGV /></PublicShell>} />
                <Route path="/contrat" element={<PublicShell><Contrat /></PublicShell>} />
                <Route path="/donnees" element={<PublicShell><ProtectionDonnees /></PublicShell>} />
                <Route path="/rgpd" element={<Navigate to="/donnees" replace />} />

                {/* Admin */}
                <Route path="/admin/login" element={<AdminShell><AdminLogin /></AdminShell>} />
                <Route path="/admin" element={<ProtectedRoute><AdminShell><AdminDashboard /></AdminShell></ProtectedRoute>} />
                <Route path="/admin/studio" element={<ProtectedRoute><AdminShell><AdminStudioProjects /></AdminShell></ProtectedRoute>} />
                <Route path="/admin/blog" element={<ProtectedRoute><AdminShell><AdminBlog /></AdminShell></ProtectedRoute>} />
                {/* Editor route aliases — "Nouvel article" CTA points here */}
                <Route path="/admin/new" element={<ProtectedRoute><AdminShell><AdminEditor /></AdminShell></ProtectedRoute>} />
                <Route path="/admin/blog/nouveau" element={<ProtectedRoute><AdminShell><AdminEditor /></AdminShell></ProtectedRoute>} />
                <Route path="/admin/edit/:id" element={<ProtectedRoute><AdminShell><AdminEditor /></AdminShell></ProtectedRoute>} />
                <Route path="/admin/mssante" element={<ProtectedRoute><AdminShell><AdminMssante /></AdminShell></ProtectedRoute>} />
                <Route path="/admin/demos" element={<ProtectedRoute><AdminShell><AdminDemoRequests /></AdminShell></ProtectedRoute>} />
                {/* "Planifier une démo" CTA from the overview points here (lists pending demos) */}
                <Route path="/admin/demos/nouvelle" element={<ProtectedRoute><AdminShell><AdminDemoRequests /></AdminShell></ProtectedRoute>} />
                <Route path="/admin/support" element={<ProtectedRoute><AdminShell><AdminSupport /></AdminShell></ProtectedRoute>} />
                <Route path="/admin/billing" element={<ProtectedRoute><AdminShell><AdminBilling /></AdminShell></ProtectedRoute>} />
                <Route path="/admin/facturation" element={<ProtectedRoute><AdminShell><AdminBilling /></AdminShell></ProtectedRoute>} />
                <Route path="/admin/seo" element={<ProtectedRoute><AdminShell><AdminSeo /></AdminShell></ProtectedRoute>} />
                <Route path="/admin/demandes-mss" element={<ProtectedRoute><AdminShell><AdminDemandesMss /></AdminShell></ProtectedRoute>} />

                {/* 404 */}
                <Route path="/contact" element={<Navigate to="/demo" replace />} />
                <Route path="*" element={<PublicShell><NotFound /></PublicShell>} />
              </Routes>
              <CookieBanner />
              <Toaster richColors position="top-right" />
            </BrowserRouter>
          </div>
            </ImProvider>
        </ProAuthProvider>
      </AuthProvider>
      </GlobalErrorBoundary>
    </HelmetProvider>
  );
}
